<template>
  <main class="page-campaigns">
    <div class="container-wide">
      <CampaignsHeadline
        page="root"
        :breadcrumbs="getBreadcrumbs()"
        @adgroups-close="onAdGroupClose()"
        @ads-close="onAdClose()"
        @toggle-add-ad-account="toggleAddAdAccount"
      />
      <mq-layout mq="lg">
        <CampaignsFilters />
      </mq-layout>
      <CampaignsGraphs v-show="showGraphs && !graphsHiddenOnScroll" />
      <CampaignsGrid v-show="!adGroups" @adgroups-loaded="onAdGroupsLoaded" />
      <CampaignsAdGroupsGrid
        v-if="adGroups && !ads && !keywords"
        :ad-groups="adGroups"
        @adgroups-close="onAdGroupClose()"
        @ads-loaded="onAdsLoaded"
      />
      <CampaignsAdsGrid
        v-if="ads && !keywords"
        :ads="ads"
        @ads-close="onAdClose()"
        @keywords-loaded="onKeywordsLoaded"
        @toggle-facebook-campaign-details="toggleFacebookCampaignDetails"
      />
      <CampaignsKeywordsGrid
        v-if="keywords"
        :keywords="keywords"
        @keywords-close="onKeywordsClose()"
        @toggle-google-campaign-details="toggleGoogleCampaignDetails"
      />
      <transition name="fade">
        <AddAdAccount v-if="addAdAccountVisible" @toggle-add-ad-account2="toggleAddAdAccount" />
      </transition>
      <transition name="fade">
        <FacebookCampaignDetails
          v-if="facebookCampaignDetailsVisible"
          :ad="selectedAd"
          :ad-group="adGroup"
          @toggle-facebook-campaign-details2="toggleFacebookCampaignDetails"
        />
      </transition>
      <transition name="fade">
        <GoogleCampaignDetails
          v-if="googleCampaignDetailsVisible"
          :ad="selectedAd"
          :ad-group="adGroup"
          @toggle-google-campaign-details2="toggleGoogleCampaignDetails"
        />
      </transition>
    </div>
  </main>
</template>

<script lang="ts">
import { CancelTokenSource } from "axios";
import moment from "moment";
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import CampaignsAdGroupsGrid from "@/components/Campaigns/CampaignsAdGroupsGrid.vue";
import CampaignsAdsGrid from "@/components/Campaigns/CampaignsAdsGrid.vue";
import CampaignsKeywordsGrid from "@/components/Campaigns/CampaignsKeywordsGrid.vue";
import { Breadcrumb, BreadcrumbAction } from "@/models/Breadcrumb";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { MultiselectItem } from "@/models/MultiselectItem";
import { TableSettings, TableSettingsResponse, TableSettingsState } from "@/models/TableSettings";
import { CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignAdGridViewModel } from "@/models/campaigns/CampaignAdGridViewModel";
import { CampaignAdGroupGridViewModel } from "@/models/campaigns/CampaignAdGroupGridViewModel";
import { CampaignGridView } from "@/models/campaigns/CampaignGridView";
import { CampaignKeywordGridViewModel } from "@/models/campaigns/CampaignKeywordGridViewModel";
import { CampaignsStatisticsByDate } from "@/models/campaigns/CampaignsStatisticsByDate";
import { CampaignsStats } from "@/models/campaigns/CampaignsStats";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { RefreshGridMode } from "@/models/leads/ContactForm";
import CampaignService from "@/services/CampaignService";
import HelperService from "@/services/HelperService";
import ApiService, { CommonMultiselectPayload } from "@/services/api/ApiService";
import { CampaignsPayload } from "@/services/api/CampaignsApiService";
import AddAdAccount from "../components/Campaigns/AddAdAccount.vue";
import CampaignsFilters from "../components/Campaigns/CampaignsFilters.vue";
import CampaignsGraphs from "../components/Campaigns/CampaignsGraphs.vue";
import CampaignsGrid from "../components/Campaigns/CampaignsGrid.vue";
import CampaignsHeadline from "../components/Campaigns/CampaignsHeadline.vue";
import FacebookCampaignDetails from "../components/Campaigns/FacebookCampaignDetails.vue";
import GoogleCampaignDetails from "../components/Campaigns/GoogleCampaignDetails.vue";
import GridTableSettingsEn from "../data/grid-defaultsettings-en.json";
import GridTableSettingsHe from "../data/grid-defaultsettings-he.json";
import { Meta } from "../decorators/MetaDecorator";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});
const namespace = "campaignsModule";

@Component({
  components: {
    AddAdAccount,
    CampaignsAdGroupsGrid,
    CampaignsAdsGrid,
    CampaignsHeadline,
    CampaignsFilters,
    CampaignsGraphs,
    CampaignsGrid,
    CampaignsKeywordsGrid,
    FacebookCampaignDetails,
    GoogleCampaignDetails,
  },
})
export default class Campaigns extends Vue {
  @Meta
  myMetaConfig() {
    return {
      title: i18n.t("Campaigns.Title").toString()
    };
  }

  //#region Vuex getters and actions
  
  @Getter("campaigns", { namespace }) campaigns!: DataSourceResult;
  @Getter("campaignsCancelTokenSource", { namespace }) campaignsCancelTokenSource!: CancelTokenSource | undefined;
  @Getter("campaignsPageData", { namespace }) campaignsPageData!: CampaignsPageViewModel;
  @Getter("columnsFilters", { namespace }) columnsFilters!: FilterCollection;
  @Getter("datePeriod", { namespace }) datePeriod!: Date[];
  @Getter("graphsHiddenOnScroll", { namespace }) graphsHiddenOnScroll!: boolean;
  @Getter("isMediaChannel", { namespace }) isMediaChannel!: boolean;
  @Getter("needToRefreshCampaignsGrid", { namespace }) needToRefreshCampaignsGrid!: RefreshGridMode;
  @Getter("page", { namespace }) page!: number;
  @Getter("searchValue", { namespace }) searchValue!: string;
  @Getter("selectedCampaign", { namespace }) selectedCampaign!: CampaignGridView;
  @Getter("selectedCampaignsIds", { namespace }) selectedCampaignsIds!: number[];
  @Getter("selectedCampaignStatus", { namespace }) selectedCampaignStatus!: CampaignStatus | null;
  @Getter("selectedChannelMetadataTypesIds", { namespace }) selectedChannelMetadataTypesIds!: number[];
  @Getter("selectedClientsIds", { namespace }) selectedClientsIds!: number[];
  @Getter("selectedProjectsIds", { namespace }) selectedProjectsIds!: number[];
  @Getter("showGraphs", { namespace }) showGraphs!: boolean;
  @Getter("sortDir", { namespace }) sortDir!: string;
  @Getter("sortField", { namespace }) sortField!: string;
  @Action("getCampaigns", { namespace }) getCampaigns!: (payload: CampaignsPayload) => Promise<DataSourceResult>;
  @Action("getCampaignsPageData", { namespace }) getCampaignsPageData!: () => Promise<CampaignsPageViewModel>;
  @Action("getCampaignsStatsByDate", { namespace }) getCampaignsStatsByDate!: (payload: CampaignsPayload) => Promise<CampaignsStatisticsByDate[]>;
  @Action("getCampaignsWithStats", { namespace }) getCampaignsWithStats!: (payload: CampaignsPayload) => Promise<CampaignGridView[]>;
  @Action("getClients", { namespace }) getClients!: () => Promise<MultiselectItem[]>;
  @Action("getProjects", { namespace }) getProjects!: (payload: CommonMultiselectPayload) => Promise<MultiselectItem[]>;
  @Action("setCampaignsPayload", { namespace }) setCampaignsPayload!: (campaignsPayload: CampaignsPayload) => void;
  @Action("setCampaignsStatsByDate", { namespace }) setCampaignsStatsByDate!: (campaignsStatsByDate: CampaignsStatisticsByDate[] | null) => void;
  @Action("setCampaignsWithStats", { namespace }) setCampaignsWithStats!: (stats: CampaignsStats | null) => void;
  @Action("setNeedToRefreshCampaignsGrid", { namespace }) setNeedToRefreshCampaignsGrid!: (needToRefreshCampaignsGrid: RefreshGridMode) => void;
  @Action("setPage", { namespace }) setPage!: (page: number) => void;
  @Action("setSelectedCampaign", { namespace }) setSelectedCampaign!: (selectedCampaign?: CampaignGridView | null) => void;
  @Action("setSelectedClientsIds", { namespace }) setSelectedClientsIds!: (selectedClientsIds: number[]) => void;
  @Action("setTableSettings", { namespace }) setTableSettings!: (tableSettings: TableSettings[] | undefined) => void;
  
  //#endregion Vuex getters and actions


  //#region Properties

  PAGE_FILTERS_COOKIE_NAME = "page_filters";
  public addAdAccountVisible = false;
  public facebookCampaignDetailsVisible = false;
  public googleCampaignDetailsVisible = false;
  token = "";
  adGroup: CampaignAdGroupGridViewModel | null = null;
  selectedAd: CampaignAdGridViewModel | null = null;
  adGroups: CampaignAdGroupGridViewModel[] | null = null;
  ads: CampaignAdGridViewModel[] | null = null;
  keywords: CampaignKeywordGridViewModel[] | null = null;

  //#endregion Properties


  //#region Watchers

  @Watch("datePeriod", { immediate: true })
  onDatePeriodChange(newDatePeriod: Date[], oldDatePeriod: Date[]): void {
    if (oldDatePeriod === undefined || newDatePeriod === undefined || oldDatePeriod.length === 0
          || moment(newDatePeriod[0]).format("YYYY-MM-DD") === moment(oldDatePeriod[0]).format("YYYY-MM-DD")
            && moment(newDatePeriod[1]).format("YYYY-MM-DD") === moment(oldDatePeriod[1]).format("YYYY-MM-DD")) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("needToRefreshCampaignsGrid", { immediate: true })
  onNeedToRefreshCampaignsGrid(newValue: RefreshGridMode, oldValue: RefreshGridMode): void {
    if (oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(newValue);
    }
  }

  @Watch("searchValue", { immediate: true })
  onSearchValueChange(newValue: string, oldValue: string): void {
    if (oldValue === undefined) {
      return;
    } else {
      this.refreshGrid();
    }
  }

  @Watch("showGraphs", { immediate: true })
  onshowGraphsChange(newValue: boolean, oldValue: boolean): void {
    if (newValue === oldValue || oldValue === undefined) {
      return;
    } else if (newValue) {
      this.refreshGrid(RefreshGridMode.GraphsOnly);
    }
  }

  //#endregion Watchers


  //#region Getters

  get commonPayload(): CommonMultiselectPayload {
    return {
      clientIds: this.selectedClientsIds,
      showNotActiveCamp: false,
      metadataIds: this.selectedChannelMetadataTypesIds,
      statusIds: [],
      channelIds: [],
    };
  }

  //#endregion Getters


  //#region Lifecycle

  mounted(): void {
    this.token = HelperService.guid();
    const cookieValue: string = Vue.$cookies.get(this.PAGE_FILTERS_COOKIE_NAME);
    if (!cookieValue) {
      CampaignService.resetFilters(false);
    }
    this.getCampaignsPageData().then((i: CampaignsPageViewModel) => {
      this.getClients();
      if (i.ClientId) {
        this.setSelectedClientsIds([i.ClientId]);
      } else {
        this.getProjects(this.commonPayload);
        this.refreshGrid();
      }
    });
    this.setTableSettings([]);
    this.setSelectedCampaign(null);
    ApiService.getTableSettings(i18n.locale, this.$route.path).then((data: TableSettingsResponse) => {
      if (data.settings.length > 0) {
        data.settings.forEach((settings: TableSettings) => {
          settings.StateObject = JSON.parse(settings.State);
        });
        this.setTableSettings(data.settings);
      } else {
        let settingsState: TableSettingsState = GridTableSettingsHe;
        switch (i18n.locale) {
          case "en-US":
            settingsState = GridTableSettingsEn;
            break;
          case "he-IL":
            settingsState = GridTableSettingsHe;
            break;
        }
        this.setTableSettings([{
          Culture: i18n.locale,
          GridId: "channelsgrid",
          State: JSON.stringify(settingsState),
          StateObject: settingsState,
          Url: location.pathname
        }, {
          Culture: i18n.locale,
          GridId: "ad_groups__groups",
          State: JSON.stringify(settingsState),
          StateObject: settingsState,
          Url: location.pathname
        }]);
      }
    });
  }

  //#endregion Lifecycle


  //#region Handlers
  
  public addAdAccountPopupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public addAdAccountPopupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public facebookCampaignDetailsPopupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public facebookCampaignDetailsPopupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  public googleCampaignDetailsPopupBodyOff(): void {
    const scrollY = document.body.style.top;
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
  }

  public googleCampaignDetailsPopupBodyOn(): void {
    document.body.style.top = "-" + window.scrollY + "px";
    document.body.style.position = "fixed";
  }

  onAdClose() {
    this.adGroup = null;
    this.ads = null;
    this.keywords = null;
  }

  onAdGroupClose() {
    this.adGroup = null;
    this.adGroups = null;
    this.ads = null;
    this.keywords = null;
    this.setSelectedCampaign(null);
  }

  onAdGroupsLoaded(adGroups: CampaignAdGroupGridViewModel[]) {
    this.adGroups = adGroups;
  }

  onAdsLoaded(ads: CampaignAdGridViewModel[], adGroup: CampaignAdGroupGridViewModel) {
    this.adGroup = adGroup;
    this.ads = ads;
  }

  onKeywordsClose() {
    this.keywords = null;
  }

  onKeywordsLoaded(keywords: CampaignKeywordGridViewModel[], ad: CampaignAdGridViewModel) {
    this.keywords = keywords;
    this.selectedAd = ad;
  }

  public toggleAddAdAccount(): void {
    this.addAdAccountVisible = !this.addAdAccountVisible;
    this.addAdAccountVisible ? this.addAdAccountPopupBodyOn() : this.addAdAccountPopupBodyOff();
  }

  public toggleFacebookCampaignDetails(ad: CampaignAdGridViewModel): void {
    this.selectedAd = ad;
    this.facebookCampaignDetailsVisible = !this.facebookCampaignDetailsVisible;
    this.facebookCampaignDetailsVisible
      ? this.facebookCampaignDetailsPopupBodyOn()
      : this.facebookCampaignDetailsPopupBodyOff();
  }

  public toggleGoogleCampaignDetails(): void {
    this.googleCampaignDetailsVisible = !this.googleCampaignDetailsVisible;
    this.googleCampaignDetailsVisible ? this.googleCampaignDetailsPopupBodyOn() : this.googleCampaignDetailsPopupBodyOff();
  }

  //#endregion Handlers


  //#region Private methods

  public getBreadcrumbs(): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = [];
    if (this.campaignsPageData) {
      breadcrumbs.push({
        Action: BreadcrumbAction.Action,
        Link: "campaigns",
        Title: this.campaignsPageData.IsMediaChannel ? i18n.t("Campaigns.Type.Media").toString() : i18n.t("Campaigns.Type.Paid").toString(),
      });
    }
    if (this.selectedCampaign) {
      breadcrumbs.push({
        Action: BreadcrumbAction.Action,
        Link: "campaign",
        Title: this.selectedCampaign.ChannelTitle,
      });
    }
    if (this.adGroup) {
      breadcrumbs.push({
        Action: BreadcrumbAction.Action,
        Link: "adGroup",
        Title: this.adGroup.AdGroupTitle,
      });
    }
    return breadcrumbs;
  }

  private refreshGrid(mode: RefreshGridMode = RefreshGridMode.Full): void {
    if (this.campaignsCancelTokenSource) {
      this.campaignsCancelTokenSource?.cancel();
    }
    this.setNeedToRefreshCampaignsGrid(RefreshGridMode.None);
    if (mode !== RefreshGridMode.GridOnly) {
      this.setPage(1);
    }
    this.token = HelperService.guid();

    if (this.page === 1) {
      this.setCampaignsWithStats(null);
      this.setCampaignsStatsByDate(null);
    }
    
    const filterCollection: FilterCollection = {
      logic: "and",
      filters: [],
    };
    if (this.searchValue) {
      const searchFilters: FilterCollection = {
        logic: "or",
        filters: [],
      };
      searchFilters.filters?.push({ field: "ChannelTitle", operator: "contains", value: `'${this.searchValue}'` });
      searchFilters.filters?.push({ field: "ClientTitle", operator: "contains", value: `'${this.searchValue}'` });
      searchFilters.filters?.push({ field: "DefaultAssetTitle", operator: "contains", value: `'${this.searchValue}'` });
      searchFilters.filters?.push({ field: "DefaultAssetUrl", operator: "contains", value: `'${this.searchValue}'` });
      filterCollection.filters?.push(searchFilters);
    }
    if (this.columnsFilters && this.columnsFilters.filters && this.columnsFilters.filters?.length > 0) {
      filterCollection.filters?.push(this.columnsFilters);
    }
    if (this.campaignsPageData?.ClientId) {
      filterCollection.filters?.push(HelperService.setFilterArray(["ClientID"], [this.campaignsPageData.ClientId], "eq"));
    } else if (this.selectedClientsIds.length) {
        filterCollection.filters?.push(HelperService.setFilterArray(["ClientID"], this.selectedClientsIds, "eq"));
    }
    if (this.selectedChannelMetadataTypesIds.length) {
      filterCollection.filters?.push(HelperService.setFilterArray(["ChannelMetadataTypeId"], this.selectedChannelMetadataTypesIds, "eq"));
    }
    if (this.selectedCampaignStatus !== undefined && this.selectedCampaignStatus !== null) {
      filterCollection.filters?.push({
        field: "ChannelStatus",
        operator: "eq",
        value: this.selectedCampaignStatus.toString(),
      });
    }
    if (this.isMediaChannel) {
      // filterCollection.filters?.push({
      //   field: "Active",
      //   operator: "eq",
      //   value: "true",
      // });
      // filterCollection.filters?.push({
      //   field: "Connected",
      //   operator: "eq",
      //   value: "true",
      // });
      // filterCollection.filters?.push({
      //   field: "TokenValid",
      //   operator: "eq",
      //   value: "true",
      // });
    }
    
    const filtersString: string = HelperService.getFiltersString(filterCollection);
    const payload: CampaignsPayload = {
      campaignsIds: this.selectedCampaignsIds,
      dateFrom: this.datePeriod[0] ? moment(this.datePeriod[0]).format("YYYY-MM-DD") : undefined,
      dateTo: this.datePeriod[0] ? moment(this.datePeriod[1]).format("YYYY-MM-DD") : undefined,
      filter: filtersString,
      isMediaChannel: this.isMediaChannel,
      page: this.page,
      pageSize: CampaignService.getPageSize(),
      sort: this.sortField && this.sortDir ? `${ this.sortField }-${ this.sortDir }` : "",
      projects: this.selectedProjectsIds,
      token: this.token,
    };
    this.setCampaignsPayload(payload);
    if (mode === RefreshGridMode.GraphsOnly) {
      this.refreshStatistics(payload, filtersString);
    } else {
      this.getCampaigns(payload).finally(() => {
        this.setNeedToRefreshCampaignsGrid(RefreshGridMode.None);
      });
      if (mode === RefreshGridMode.Full) {
        if (this.showGraphs) {
          this.refreshStatistics(payload, filtersString);
        }
      }
    }

    Vue.$cookies.set(this.PAGE_FILTERS_COOKIE_NAME, true, "8h");
  }

  private refreshStatistics(payload: CampaignsPayload, filtersString: string): void {
    payload.page = 1;
    payload.pageSize = Math.pow(2,31) - 1;
    this.getCampaignsWithStats(payload);
    this.getCampaignsStatsByDate(payload);
  }

  //#endregion Private methods
}

</script>
